import { useState, useEffect } from "react";
import { z } from "zod";
import { useDynamicConfig } from "../../../../lib/delivery";
import { DynamicConfigs } from "../../../../lib/delivery_keys";

const MaintenanceWindowSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  title: z.string().optional(),
  description: z.string().optional(),
});

const MaintenanceConfigSchema = z.object({
  windows: z.array(MaintenanceWindowSchema).default([]),
});

export type MaintenanceWindow = z.infer<typeof MaintenanceWindowSchema>;
export type MaintenanceConfig = z.infer<typeof MaintenanceConfigSchema>;

const POLL_MS = 24 * 60 * 60 * 1000;
const EMPTY_WINDOWS: MaintenanceWindow[] = [];

function findActiveWindow(windows: MaintenanceWindow[]): MaintenanceWindow | null {
  const now = Date.now();
  return windows.find((w) => w.start.getTime() <= now && now < w.end.getTime()) ?? null;
}

/** Smallest future boundary across all windows (a start or end timestamp), or null if none. */
function nextBoundary(windows: MaintenanceWindow[]): number | null {
  const now = Date.now();
  const candidates = windows
    .flatMap((w) => [w.start.getTime(), w.end.getTime()])
    .filter((t) => t > now);
  return candidates.length > 0 ? Math.min(...candidates) : null;
}

/**
 * Returns the currently active maintenance window, or null.
 * Polls the Statsig config once per day; schedules precise timeouts at each
 * window boundary so active sessions transition without a manual reload.
 */
export function useMaintenanceWindow(defaultConfig?: MaintenanceConfig): MaintenanceWindow | null {
  const config =
    useDynamicConfig(DynamicConfigs.maintenanceWindows, MaintenanceConfigSchema, POLL_MS) ??
    defaultConfig ??
    null;

  // tick increments at each start/end boundary to trigger a re-evaluation.
  const [tick, setTick] = useState(0);

  const windows = config?.windows ?? EMPTY_WINDOWS;

  useEffect(() => {
    const boundary = nextBoundary(windows);
    if (boundary === null) return;
    const id = setTimeout(() => setTick((t) => t + 1), boundary - Date.now());
    return () => clearTimeout(id);
  }, [windows, tick]);

  return findActiveWindow(windows);
}
